import { combineLatest, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { BrowserNtfEventType, BrowserNtfObj } from 'src/lib/fliko/models/browserNtf/browserNtf.data';
import { LocalizationHelper } from 'src/app/shared/helpers/localization.helper';
import { BrowserNtfHistoryService } from 'src/app/shared/services/browser-ntf/browser-ntf-history.service';
import { BrowserNtfNavigationService } from 'src/app/shared/services/browser-ntf/browser-ntf-navigation.service';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { RWDService } from '../../services/rwd.service';
import {
  BrowserNtfFilters,
  BrowserNtfPanelData,
  BrowserNtfTypeMeta,
  BrowserNtfTypeOption,
  BrowserNtfVM
} from './browser-ntf-panel.models';

@Component({
  selector: 'app-browser-ntf-panel',
  templateUrl: './browser-ntf-panel.component.html',
  styleUrls: ['./browser-ntf-panel.component.scss']
})
export class BrowserNtfPanelComponent implements OnInit, OnDestroy {
  public readonly ALL = 'all';

  public readonly filterForm = this.fb.group({
    type: this.ALL,
    estateId: this.ALL,
    search: '',
    unreadOnly: false
  });

  public typeOptions: BrowserNtfTypeOption[] = [];
  public estateOptions: BrowserNtfTypeOption[] = [];
  public notifications: BrowserNtfVM[] = [];
  public filteredNotifications: BrowserNtfVM[] = [];
  public loading = false;
  public hasMore = false;
  public unreadCount = 0;

  private readonly subscription$ = new Subscription();
  private readonly locHelper = new LocalizationHelper(this.ls);
  private readonly estateNameMap = new Map<string, string>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: BrowserNtfPanelData,
    public rwdService: RWDService,
    private dialogRef: MatDialogRef<BrowserNtfPanelComponent>,
    private fb: FormBuilder,
    private historyService: BrowserNtfHistoryService,
    private navigationService: BrowserNtfNavigationService,
    private ls: LocalizationService
  ) {}

  ngOnInit(): void {
    this.prepareOptions();
    this.observeState();
    void this.historyService.refresh(this.data.eventTypes);
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  public close(): void {
    this.dialogRef.close();
  }

  public refresh(): void {
    void this.historyService.refresh(this.data.eventTypes);
  }

  public loadMore(): void {
    void this.historyService.loadMore(this.data.eventTypes);
  }

  public markAllAsRead(): void {
    if (!this.unreadCount) return;
    void this.historyService.setAllDisplayed(true, this.data.eventTypes);
  }

  public toggleDisplayed(vm: BrowserNtfVM, event: MouseEvent): void {
    event.stopPropagation();
    void this.historyService.setDisplayed(vm.notification, !vm.notification.displayed);
  }

  public openDetails(vm: BrowserNtfVM): void {
    if (!vm.notification.displayed) {
      void this.historyService.setDisplayed(vm.notification, true);
    }

    this.navigationService.navigateToNotificationUrl(vm.notification.deepLink, vm.notification.estateId ?? undefined);
    this.close();
  }

  public trackByNotification(_: number, vm: BrowserNtfVM): string {
    return vm.notification.id;
  }

  private prepareOptions(): void {
    const categories = this.data.eventTypes?.length
      ? [...new Set(this.data.eventTypes.map((eventType) => this.getTypeMeta(eventType).group))]
      : ['chat', 'defect', 'residentIssue', 'booking', 'calendar', 'deadline', 'access', 'system', 'concierge', 'offer'];
    const estates = [...(this.data.estates ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    estates.forEach((estate) => this.estateNameMap.set(estate.id, estate.name));

    this.estateOptions = [
      { value: this.ALL, label: this.ls.translate('browserNtf.panel.allEstates') },
      ...estates.map((estate) => ({ value: estate.id, label: estate.name }))
    ];

    this.typeOptions = [
      { value: this.ALL, label: this.ls.translate('browserNtf.panel.allTypes') },
      ...categories.map((value) => ({ value, label: this.ls.translate('browserNtf.panel.type.' + value) }))
    ];
  }

  private observeState(): void {
    const notifications$ = this.data.eventTypes?.length
      ? this.historyService.notificationsFor$(this.data.eventTypes)
      : this.historyService.notifications$;
    const loading$ = this.data.eventTypes?.length
      ? this.historyService.loadingFor$(this.data.eventTypes)
      : this.historyService.loading$;
    const hasMore$ = this.data.eventTypes?.length
      ? this.historyService.hasMoreFor$(this.data.eventTypes)
      : this.historyService.hasMore$;

    this.subscription$.add(loading$.subscribe((loading) => (this.loading = loading)));
    this.subscription$.add(hasMore$.subscribe((hasMore) => (this.hasMore = hasMore)));

    this.subscription$.add(
      combineLatest([
        notifications$,
        this.filterForm.valueChanges.pipe(startWith(this.filterForm.value))
      ]).subscribe(([notifications, filters]) => {
        const { type, estateId, search, unreadOnly } = filters as BrowserNtfFilters;

        this.unreadCount = notifications.filter((notification) => !notification.displayed).length;
        this.notifications = notifications.map((notification) => this.toViewModel(notification));
        this.filteredNotifications = this.filterNotifications(
          this.notifications,
          type || this.ALL,
          estateId || this.ALL,
          search || '',
          !!unreadOnly
        );
      })
    );
  }

  private filterNotifications(
    notifications: BrowserNtfVM[],
    type: string,
    estateId: string,
    search: string,
    unreadOnly: boolean
  ): BrowserNtfVM[] {
    const searchText = this.clearDiacriticsAndToLowerCase(search);

    return notifications.filter((vm) => {
      if (type !== this.ALL && vm.group !== type) return false;
      if (estateId !== this.ALL && vm.notification.estateId !== estateId) return false;
      if (unreadOnly && vm.notification.displayed) return false;
      if (searchText && !vm.searchText.includes(searchText)) return false;
      return true;
    });
  }

  private toViewModel(notification: BrowserNtfObj): BrowserNtfVM {
    const meta = this.getTypeMeta(notification.eventType);
    const estateName = notification.estateName?.trim() || this.getEstateName(notification.estateId);
    const category = this.ls.translate(meta.categoryKey);
    const action = this.ls.translate(meta.actionKey);
    const title = notification.title ?? '';
    const body = notification.body ?? '';

    return {
      notification,
      group: meta.group,
      category,
      action,
      icon: meta.icon,
      estateName,
      timeLabel: this.getTimeLabel(notification.createDate),
      searchText: this.clearDiacriticsAndToLowerCase(
        [category, action, title, body, estateName, notification.eventType ?? ''].join(' ')
      )
    };
  }

  private getTypeMeta(eventType: BrowserNtfObj['eventType']): BrowserNtfTypeMeta {
    switch (eventType) {
      case BrowserNtfEventType.CHAT_NEW:
        return this.meta('chat', 'newMessage', 'question_answer');
      case BrowserNtfEventType.DEFECT_NEW:
        return this.meta('defect', 'newDefect', 'build');
      case BrowserNtfEventType.DEFECT_UPDATE:
        return this.meta('defect', 'updated', 'build');
      case BrowserNtfEventType.ISSUE_NEW:
        return this.meta('residentIssue', 'newIssue', 'report_problem');
      case BrowserNtfEventType.ISSUE_UPDATE:
        return this.meta('residentIssue', 'updated', 'report_problem');
      case BrowserNtfEventType.BOOKING_NEW:
        return this.meta('booking', 'newBooking', 'event_available');
      case BrowserNtfEventType.CALENDAR_EVENT:
        return this.meta('calendar', 'event', 'calendar_today');
      case BrowserNtfEventType.DEADLINE_UPCOMING:
        return this.meta('deadline', 'upcoming', 'notification_important');
      case BrowserNtfEventType.SYSTEM_VERSION:
        return this.meta('system', 'version', 'system_update');
      case BrowserNtfEventType.CONCIERGE_NEW:
        return this.meta('concierge', 'newTask', 'room_service');
      case BrowserNtfEventType.CONCIERGE_UPDATE:
        return this.meta('concierge', 'updated', 'room_service');
      case BrowserNtfEventType.ACCESS_REQUEST_MOBILE:
      case BrowserNtfEventType.ACCESS_REQUEST_CRM:
        return this.meta('access', 'request', 'person_add');
      case BrowserNtfEventType.OFFER_RESERVATION_NEW:
        return this.meta('offer', 'newOfferReservation', 'event_available');
      default:
        return this.meta('other', 'notification', 'notifications_active');
    }
  }

  private meta(typeKey: string, actionKey: string, icon: string): BrowserNtfTypeMeta {
    return {
      group: typeKey,
      categoryKey: `browserNtf.panel.type.${typeKey}`,
      actionKey: `browserNtf.panel.action.${actionKey}`,
      icon
    };
  }

  private getEstateName(estateId?: string | null): string {
    if (!estateId) return '';
    return this.estateNameMap.get(estateId) ?? estateId;
  }

  private getTimeLabel(createDate: number): string {
    if (!Number.isFinite(createDate) || createDate <= 0) return '';

    const diffMs = Date.now() - createDate;
    if (Math.abs(diffMs) < 60_000) return this.ls.translate('browserNtf.toast.now');

    return this.locHelper.relativeTime(createDate);
  }

  private clearDiacriticsAndToLowerCase(value: string): string {
    return StrHlp.clearDiacritics(value.toLowerCase());
  }
}
